import {
  Condition,
  ConditionType,
  FormulaTraitConfig,
  getPropertyType,
  isAggregationTraitConfig,
  isColumnTypeValue,
  isCountDedupedTraitConfig,
  isIntervalType,
  isJsonPathColumn,
  isJourneyMemberStateColumn,
  isOrderDedupedTraitConfig,
  isTimeRangeType,
  isTransformedColumn,
  NumberOperator,
  NumberRangeValue,
  RelativeDirection,
  StringOperator,
  SyntheticColumn,
  TimeRangeField,
  TimeRangeValue,
  TimestampGranularity,
  TimeType,
  TraitConfig,
  TransformedColumn,
} from "@hightouch/lib/query/visual/types";
import { Box, Row, Text, TraitIcon, TextWithTooltip } from "@hightouch/htui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useQueryBuilderContext } from "src/components/explore/context/hooks";
import { isDenied } from "src/components/explore/restrictions";
import { groupBy, isEqual } from "lodash";
import * as Yup from "yup";
import pluralize from "pluralize";
import { GroupColumn } from "src/components/charts/engine/types";
import {
  FilterableColumnFragment,
  TraitDefinitionWithoutFilterableColumnsFragment,
} from "src/graphql";
import { GroupByColumn } from "@hightouch/lib/query/visual/types/analytics";
import {
  ColumnComparisonOperatorsByColumnType,
  ColumnReference,
  ColumnType,
  FilterableColumn,
  getInitialTraitColumn,
  IntervalValue,
  isColumnReference,
  isFormulaTraitCondition,
  isInlineTraitCondition,
  isPropertyCondition,
  isRelatedColumn,
  isRelatedJourneyEventColumn,
  isSyntheticColumn,
  isTraitColumn,
  isTraitCondition,
  JourneyEventColumn,
  Operator,
  OperatorOptions,
  OperatorsWithoutValue,
  PropertyCondition,
  RawColumn,
  RawSqlTraitConfig,
  RelatedColumn,
  shouldResetPercentile,
  shouldResetTimeType,
  shouldResetValue,
  TraitDefinition,
  TraitType,
  JourneySetCondition,
  journeySetWindowTimestampOperatorOptionsLabelMap,
  WithinOperators,
} from "src/types/visual";
import { Option } from "src/ui/select";
import { accurateCommaNumber, ordinalSuffix } from "src/utils/numbers";
import { formatDateOrDatetime, formatTime } from "src/utils/time";
import { isPresent } from "ts-extras";
import { FilterColumnOption } from "src/components/explore/filter-popover/types";
import {
  columnReferenceKey,
  getPropertyLabel,
} from "src/components/explore/filter-popover/utils";
import { findDuplicates } from "src/utils/find-duplicates";

export interface ColumnOption {
  value: any;
  label: string;
  description?: string | null;
  type: ColumnType;
  case_sensitive: boolean | null;
}

export interface TraitOption {
  value: RelatedColumn | TransformedColumn;
  label: string;
  description?: string;
  operator: "exists";
  type: ColumnType | undefined;
  render: () => JSX.Element;
}

const emptyValue = "___";

/**
 * Checks if a property condition is comparing to a trait column.
 *
 * Handles two states:
 * 1. Direct TraitColumn - intermediate placeholder when user selects "trait"
 *    comparison type but hasn't selected a specific trait yet
 * 2. Wrapped TraitColumn - final state after user selects a specific trait,
 *    wrapped in RelatedColumn (aggregation traits) or TransformedColumn (formula traits)
 */
export const isConditionComparingToTrait = (
  condition: PropertyCondition,
): boolean => {
  if (!condition.value || !isColumnTypeValue(condition.value)) {
    return false;
  }

  const property = condition.value.property;
  if (!property || typeof property === "string") {
    return false;
  }

  if (isTraitColumn(property)) {
    return true;
  }

  if (
    (isRelatedColumn(property) || isTransformedColumn(property)) &&
    isTraitColumn(property.column)
  ) {
    return true;
  }

  return false;
};

export const getColumnFromValue = <
  T extends ColumnOption | TraitOption | FilterColumnOption,
>(
  options: T[],
  column: any,
) => {
  return options.find(({ value }) => column === value);
};

export const getColumnOption =
  (
    isDeprecatedPropertyCondition?: boolean,
    duplicateColumnNames?: Set<string>,
  ) =>
  (column: FilterableColumn): ColumnOption => {
    const {
      alias,
      name,
      type,
      custom_type,
      column_reference,
      case_sensitive,
      description,
      merged_relationship_names,
    } = column;
    return {
      value: isDeprecatedPropertyCondition
        ? getPropertyNameFromColumn(column_reference)
        : column_reference,
      label: getPropertyLabel(
        alias,
        name,
        duplicateColumnNames?.has(name) ? merged_relationship_names : [],
      ),
      description: description || undefined,
      type: (custom_type || type) as ColumnType,
      case_sensitive,
    };
  };

export const getColumnOptions = (
  columns: FilterableColumn[],
  isDeprecatedPropertyCondition?: boolean,
): ColumnOption[] => {
  const duplicateColumnNames = findDuplicates(columns, (c) => c.name);
  const result = columns.map((column) =>
    getColumnOption(
      isDeprecatedPropertyCondition,
      duplicateColumnNames,
    )(column),
  );

  result.sort((option1, option2) => option1.label.localeCompare(option2.label));

  return result;
};

export const getRawColumn = (
  column: RawColumn | RelatedColumn | SyntheticColumn | TransformedColumn,
): RawColumn | null => {
  if (!column || isSyntheticColumn(column) || isTransformedColumn(column)) {
    return null;
  }

  if (column.type === "raw") {
    return column;
  }

  if (column.type === "related") {
    return column.column.type === "raw" ? column.column : null;
  }

  return null;
};

export const getModelIdFromColumn = (
  column: ColumnReference | null | undefined,
): string | null => {
  if (!column) {
    return null;
  }

  if (column.type === "raw") {
    return column.modelId?.toString();
  } else if (column.type === "related") {
    return getModelIdFromColumn(column.column);
  }

  return null;
};

/**
 * Creates a unique identifier for a column that includes the relationship path.
 *
 * Examples:
 * - Parent column: "100:age" (modelId:columnName)
 * - Related column (single hop): "1:200:email" (relationshipId:modelId:columnName)
 * - Related column (multi-hop): "1/2:300:name" (path:modelId:columnName)
 *
 * This ensures that columns accessed through different relationship paths
 * are treated as distinct, even if they reference the same underlying column.
 */
export const getUniqueColumnIdentifier = (
  columnRef: ColumnReference | null | undefined | string,
): string => {
  if (!columnRef) {
    return "";
  }

  if (typeof columnRef === "string") {
    // For string columns, we don't have a model ID, so just return the name
    return columnRef;
  }

  // For related columns, include the path of relationship IDs
  if (columnRef.type === "related") {
    const path = columnRef.path.join("-");
    // Recursively get the identifier for the inner column
    const innerIdentifier = getUniqueColumnIdentifier(columnRef.column);
    return `${path}:${innerIdentifier}`;
  }

  // For raw columns (parent model or direct columns)
  const modelId = getModelIdFromColumn(columnRef);
  const columnName = getPropertyNameFromColumn(columnRef);

  if (modelId && columnName) {
    return `${modelId}:${columnName}`;
  }

  return columnName || "";
};

export const getModelIdFromTrait = (
  trait:
    | TraitDefinition
    | TraitDefinitionWithoutFilterableColumnsFragment
    | null
    | undefined,
): string | null => {
  if (!trait) {
    return null;
  }

  return trait.relationship?.to_model.id;
};

export const getPropertyNameFromProperty = (
  property: PropertyCondition["property"],
): string | null => {
  if (!property) {
    return null;
  }

  if (typeof property === "string") {
    return property;
  }

  return getPropertyNameFromColumn(property);
};

export const getPropertyNameFromColumn = (
  column: ColumnReference | null | undefined | string,
): null | string => {
  if (!column) {
    return null;
  }
  if (typeof column === "string") {
    return column;
  }

  // Technically this a "synthetic column", but the alias on the FE and BE are different.
  if (column.type === "model_name") {
    return "Model";
  }
  if (isSyntheticColumn(column)) {
    return column.alias ?? column.name;
  } else if (isJsonPathColumn(column)) {
    return column.path;
  } else if (column.type === "raw" || column.type === "journey_event") {
    return column.name;
  } else if (column.type === "related" || column.type === "transformed") {
    return getPropertyNameFromColumn(column.column);
  } else if (column.type === "trait") {
    return column.name ?? null;
  } else if (isJourneyMemberStateColumn(column)) {
    return "Journey entry time";
  }

  return null;
};

export const getColumnNameFromGroupByColumn = (
  column: GroupByColumn | GroupColumn,
): null | string => {
  const alias = "alias" in column ? column.alias : null;
  if (alias) return alias;

  const groupByColumn = (
    "column" in column ? column.column : column
  ) as GroupByColumn;

  return getPropertyNameFromColumn(groupByColumn);
};

export const getPropertyNameFromTrait = (
  trait:
    | TraitDefinition
    | TraitDefinitionWithoutFilterableColumnsFragment
    | null
    | undefined,
): string | null => {
  if (!trait) {
    return null;
  }

  const type = trait.type as TraitType;
  const config = trait.config as TraitConfig;

  if (
    isOrderDedupedTraitConfig(type, config) ||
    isCountDedupedTraitConfig(type, config)
  ) {
    return getPropertyNameFromColumn(config.toSelect);
  }

  if (isAggregationTraitConfig(type, config)) {
    return getPropertyNameFromColumn(config.column);
  }

  // RawSQL traits and formula traits don't have a simple property name we can extract
  return null;
};

/**
 * Generic label for trait conditions whose definition can't be resolved to a
 * name — inline traits and references to deleted or archived traits.
 */
export const UNNAMED_TRAIT_LABEL = "Trait calculation";

export const getTraitNameFromColumn = (
  column: ColumnReference,
  traits: Pick<TraitDefinition, "id" | "name">[],
): string | null => {
  if (isRelatedColumn(column) || isTransformedColumn(column)) {
    return getTraitNameFromColumn(column.column, traits);
  }

  if (isTraitColumn(column)) {
    const trait = traits.find((trait) => trait.id === column.traitDefinitionId);

    return trait?.name ?? null;
  }

  return null;
};

export const getTraitDefinitionIdFromColumn = (
  column: ColumnReference,
): string | null => {
  if (column.type === "trait") {
    return column.traitDefinitionId;
  }

  if (column.type === "related" || column.type === "transformed") {
    if (column.column.type === "trait") {
      return column.column.traitDefinitionId;
    }
  }

  return null;
};

type FormatValueOptions = {
  showParameterizedLabel?: boolean;
  prefix?: string;
};

/*
   Hardcoded dates are due to the backend implementation. Because days of week are implemented differently in different
   warehouses (usually numbers but could start at Sunday or Monday and be 0-indexed vs 1-indexed), we decided to pass
   back a date and then parse the day of week in the SQL query like EXTRACT(DOW from DATE '2021-05-05').
 */
export const dayOfWeekOptions = [
  { label: "Monday", value: "2025-05-05" },
  { label: "Tuesday", value: "2025-05-06" },
  { label: "Wednesday", value: "2025-05-07" },
  { label: "Thursday", value: "2025-05-08" },
  { label: "Friday", value: "2025-05-09" },
  { label: "Saturday", value: "2025-05-10" },
  { label: "Sunday", value: "2025-05-04" },
];

const singleDayOfWeek = (value: string) =>
  dayOfWeekOptions.find((option) => option.value === value)?.label ??
  emptyValue;

const dayOfWeekToString = (value?: string | string[]) => {
  let displayValue = emptyValue;
  if (typeof value === "string") {
    displayValue = singleDayOfWeek(value);
  }
  if (Array.isArray(value)) {
    displayValue = value.map(singleDayOfWeek).join(", ").trim();
  }
  return "day of week is " + displayValue;
};

export const formatValue = (
  condition: PropertyCondition,
  value: any,
  formatOptions: FormatValueOptions = {
    showParameterizedLabel: false,
    prefix: "",
  },
  size: "sm" | "md" = "md",
) => {
  const granularity = condition.propertyOptions?.timestampGranularity;

  const doesNotRequireValue = OperatorsWithoutValue.includes(
    condition.operator,
  );
  if (condition.propertyOptions?.parameterize && value == null) {
    // We want to differentiate between setting a condition to be parameterized (i.e., showParameterizedLabel === true)
    // vs. setting the operator and value for a condition that is parameterized (i.e., showParameterizaedLabel === false)
    if (formatOptions.showParameterizedLabel) {
      return <Text fontWeight="medium">parameterized</Text>;
    }

    return doesNotRequireValue ? null : (
      <Text fontWeight="medium" size={size}>
        ___
      </Text>
    );
  }
  if (doesNotRequireValue) {
    return null;
  }
  const propertyType = getPropertyType(condition);
  if (
    value === null ||
    value === undefined ||
    (propertyType === ColumnType.BigInt && value === "")
  ) {
    return (
      <Text fontWeight="medium" size={size}>
        ___
      </Text>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return (
        <Text fontWeight="medium" size={size}>
          ___
        </Text>
      );
    }

    if (granularity === TimestampGranularity.DayOfWeek) {
      return (
        <Text isTruncated fontWeight="medium" size={size}>
          {dayOfWeekToString(value)}
        </Text>
      );
    }

    return (
      <Text isTruncated fontWeight="medium" size={size}>
        {value.map(String).join(", ")}
      </Text>
    );
  }
  if (isColumnTypeValue(value)) {
    if (isRelatedJourneyEventColumn(value.property)) {
      return (
        <Text isTruncated fontWeight="medium" size={size}>
          {formatOptions.prefix}
          {value.property.column.name || "___"}
        </Text>
      );
    }
    if (
      propertyType &&
      ColumnComparisonOperatorsByColumnType[propertyType]?.includes(
        condition.operator,
      )
    ) {
      return (
        <Text isTruncated fontWeight="medium" size={size}>
          {String(getPropertyNameFromProperty(value?.property) ?? "___")}
        </Text>
      );
    }
  }
  if (
    propertyType === ColumnType.Date ||
    propertyType === ColumnType.Timestamp
  ) {
    if (isColumnTypeValue(condition.value)) {
      return (
        <Text isTruncated size={size}>
          {String(getPropertyNameFromProperty(value?.property) ?? "___")}
        </Text>
      );
    }

    if (typeof value === "object") {
      return propertyType === ColumnType.Date
        ? getDateValue(
            value,
            condition.timeType,
            condition.propertyOptions?.timestampGranularity,
            undefined,
            size,
          )
        : getTimeValue(
            value,
            condition.timeType,
            condition.propertyOptions?.timestampGranularity,
            undefined,
            undefined,
            size,
          );
    }

    if (granularity === TimestampGranularity.Time) {
      return (
        <Text fontWeight="medium" size={size}>
          {value ? formatTime(value) : emptyValue}
        </Text>
      );
    }

    return (
      <AbsoluteTimeText
        hideTime={propertyType === ColumnType.Date}
        value={value}
        size={size}
      />
    );
  }
  if (
    typeof propertyType === "string" &&
    [ColumnType.Number, ColumnType.BigInt].includes(propertyType) &&
    condition.propertyOptions?.percentile &&
    value
  ) {
    return (
      <Text isTruncated size={size}>
        {String(value)}
        {ordinalSuffix(value)} percentile
      </Text>
    );
  }

  if (
    typeof propertyType === "string" &&
    [ColumnType.Number, ColumnType.BigInt].includes(propertyType) &&
    condition.operator === NumberOperator.Between
  ) {
    return (
      <Text size={size} isTruncated>
        {getTimeRangeValue(condition.value)}
      </Text>
    );
  }

  if (propertyType === ColumnType.Number) {
    // this format function doesn't support bigints right now
    return (
      <TextWithTooltip size={size}>
        {accurateCommaNumber(value)}
      </TextWithTooltip>
    );
  }

  return (
    <TextWithTooltip isTruncated size={size}>
      {String(value)}
    </TextWithTooltip>
  );
};

export type MinimalTraitDefinition = Pick<TraitDefinition, "type" | "config">;

export const getTraitPropertyTypeFromTraitDefinition = (
  trait: TraitDefinitionWithoutFilterableColumnsFragment,
): ColumnType => {
  switch (trait.type) {
    case TraitType.Average:
    case TraitType.Count:
    case TraitType.Sum:
      return ColumnType.Number;

    case TraitType.RawSql:
    case TraitType.Formula:
    case TraitType.Predictive:
      return (trait.config as RawSqlTraitConfig | FormulaTraitConfig)
        .resultingType;

    case TraitType.First:
    case TraitType.Last:
    case TraitType.MostFrequent:
    case TraitType.LeastFrequent:
    case TraitType.List: {
      return trait.config.resultingType;
    }
    default:
      return ColumnType.Unknown;
  }
};

export type FilterableColumnIndex = Map<string, FilterableColumn>;

/**
 * Build a key-indexed view of `filterableColumns` so callers can resolve a
 * `column_reference` in O(1) instead of an O(N) deep-equality scan. The key
 * is `columnReferenceKey(column_reference)`, which normalizes ID types
 * (e.g. `modelId`) before stringifying so a numeric and string-typed ID for
 * the same column collapse to one key — matching `isColumnReferenceEqual`.
 *
 * Hot render paths (audience builder, member table, trait pickers) should
 * build this once with `useMemo` keyed on the array reference and pass it
 * to `getTraitPropertyTypeFromIndex` inside their loops. One-shot callers
 * can keep using `getTraitPropertyType`, which transparently memoizes the
 * index via a `WeakMap` keyed on the array identity.
 */
export const buildFilterableColumnIndex = (
  filterableColumns: FilterableColumn[] | null | undefined,
): FilterableColumnIndex => {
  const index: FilterableColumnIndex = new Map();
  if (!filterableColumns) return index;
  for (const column of filterableColumns) {
    const key = columnReferenceKey(column.column_reference);
    if (!index.has(key)) index.set(key, column);
  }
  return index;
};

export interface PropertyTypeDrift {
  storedType: ColumnType;
  liveType: ColumnType;
}

/** Returns stored vs live column types when they differ; null if inconclusive. */
export const getPropertyConditionTypeDrift = (
  condition: PropertyCondition,
  filterableColumnIndex: FilterableColumnIndex | null | undefined,
): PropertyTypeDrift | null => {
  if (!filterableColumnIndex) return null;

  if (
    isTraitCondition(condition) ||
    isFormulaTraitCondition(condition) ||
    isInlineTraitCondition(condition) ||
    isJsonPathColumn(condition.property) ||
    isRelatedJourneyEventColumn(condition.property)
  ) {
    return null;
  }

  if (!isColumnReference(condition.property)) return null;

  const storedType = getPropertyType(condition);
  if (storedType == null) return null;

  const liveColumn = filterableColumnIndex.get(
    columnReferenceKey(condition.property),
  );
  if (!liveColumn) return null;

  const liveType = (liveColumn.custom_type ??
    liveColumn.type) as ColumnType | null;
  if (liveType == null) return null;

  if (liveType === storedType) return null;

  return { storedType, liveType };
};

// Cache indexes by array identity so repeated `getTraitPropertyType` calls
// against the same `filterableColumns` reference do not re-scan or re-index.
// Entries are evicted automatically when the source array is GC'd.
const filterableColumnIndexCache = new WeakMap<
  FilterableColumn[],
  FilterableColumnIndex
>();

const getCachedFilterableColumnIndex = (
  filterableColumns: FilterableColumn[] | null | undefined,
): FilterableColumnIndex | null => {
  if (!filterableColumns) return null;
  const cached = filterableColumnIndexCache.get(filterableColumns);
  if (cached) return cached;
  const index = buildFilterableColumnIndex(filterableColumns);
  filterableColumnIndexCache.set(filterableColumns, index);
  return index;
};

/**
 * O(1) variant of `getTraitPropertyType` that takes a pre-built
 * `FilterableColumnIndex` from `buildFilterableColumnIndex`. Prefer this
 * inside hot loops (per-trait, per-row) so the index is built once instead
 * of N times.
 */
export const getTraitPropertyTypeFromIndex = (
  trait: MinimalTraitDefinition,
  filterableColumnIndex: FilterableColumnIndex | null,
): ColumnType => {
  switch (trait.type) {
    case TraitType.Average:
    case TraitType.Count:
    case TraitType.Sum:
      return ColumnType.Number;

    case TraitType.RawSql:
    case TraitType.Formula:
    case TraitType.Predictive:
      return (trait.config as RawSqlTraitConfig | FormulaTraitConfig)
        .resultingType;

    case TraitType.First:
    case TraitType.Last:
    case TraitType.MostFrequent:
    case TraitType.LeastFrequent:
    case TraitType.List: {
      const columnReference = trait.config.toSelect;
      const column = filterableColumnIndex?.get(
        columnReferenceKey(columnReference),
      );
      const columnType =
        ((column?.custom_type ?? column?.type) as ColumnType) ??
        ColumnType.Unknown;
      // All other TraitTypes just use the existing columnType, but List
      // Needs to choose the right JsonArray type
      if (trait.type !== TraitType.List) {
        return columnType;
      }
      // For List, check custom_type, then infer list type
      return columnType === ColumnType.Number
        ? ColumnType.JsonArrayNumbers
        : ColumnType.JsonArrayStrings;
    }
    default:
      return ColumnType.Unknown;
  }
};

// Find the resulting type of the trait.
//
// Internally indexes `filterableColumns` by stable-stringified
// `column_reference` and caches the index on the array reference, so
// repeated calls against the same array are O(1) instead of an O(N)
// `isEqual` scan. For tight loops over many traits sharing the same
// `filterableColumns` reference, prefer building the index once with
// `buildFilterableColumnIndex` and calling `getTraitPropertyTypeFromIndex`.
export const getTraitPropertyType = (
  trait: MinimalTraitDefinition,
  filterableColumns: FilterableColumn[] | null | undefined,
): ColumnType => {
  return getTraitPropertyTypeFromIndex(
    trait,
    getCachedFilterableColumnIndex(filterableColumns),
  );
};

export const getCustomTraitPropertyType = ({
  type,
  selectedColumn,
  filterableColumns = [],
}: {
  type: TraitType;
  selectedColumn: ColumnReference | undefined;
  filterableColumns: FilterableColumn[];
}): ColumnType => {
  const propertyType = getTraitPropertyType(
    {
      type: type,
      config: {
        toSelect: selectedColumn,
      },
    },
    filterableColumns,
  );

  return propertyType;
};

export const getTraitOption =
  (property: PropertyCondition["property"]) =>
  (
    trait: TraitDefinitionWithoutFilterableColumnsFragment,
    columns: FilterableColumn[],
  ): TraitOption => {
    return {
      label: trait.name,
      description: trait.description || undefined,
      value:
        (isRelatedColumn(property) || isTransformedColumn(property)) &&
        isTraitColumn(property.column) &&
        property.column.traitDefinitionId === trait.id
          ? property
          : getInitialTraitColumn(trait),
      operator: StringOperator.Exists,
      type: getTraitPropertyType(trait, columns),
      render: () => (
        <>
          <Row as={TraitIcon} color="gray.base" boxSize={4} mr={2} />
          <Text whiteSpace="nowrap">{trait.name}</Text>
        </>
      ),
    };
  };

export const isIntervalNow = (intervalValue: IntervalValue) => {
  return intervalValue.quantity === 0;
};

export const isCompletedInterval = (intervalValue: IntervalValue) => {
  return intervalValue.interval && intervalValue.quantity != null;
};

export const isCompletedTimeRange = (timeRangeValue: TimeRangeValue) => {
  const { before, after } = timeRangeValue;
  return [before, after].every(
    (v) =>
      typeof v === "string" || (isIntervalType(v) && isCompletedInterval(v)),
  );
};

/** A TimeRangeValue whose bounds are both absolute timestamp strings. */
export type AbsoluteTimeRangeValue = Record<TimeRangeField, string>;

export type AbsoluteRangeBound = {
  field: TimeRangeField;
  value: string;
};

// Saved absolute `between` ranges may hold their bounds in either order (the
// backend sorts them internally), so the UI derives chronological order at
// render time.
export const sortedAbsoluteRangeBounds = (
  value: AbsoluteTimeRangeValue,
): [AbsoluteRangeBound, AbsoluteRangeBound] => {
  const beforeBound: AbsoluteRangeBound = {
    field: "before",
    value: value.before,
  };
  const afterBound: AbsoluteRangeBound = { field: "after", value: value.after };

  const beforeMs = Date.parse(value.before);
  const afterMs = Date.parse(value.after);

  if (Number.isNaN(beforeMs) || Number.isNaN(afterMs) || beforeMs <= afterMs) {
    return [beforeBound, afterBound];
  }
  return [afterBound, beforeBound];
};

export const AbsoluteTimeText = ({
  value,
  hideTime,
  size,
}: {
  value: any;
  hideTime?: boolean;
  size?: "sm" | "md";
}) => {
  return (
    <Text isTruncated fontWeight="medium" size={size}>
      {formatDateOrDatetime(value, !hideTime)}
    </Text>
  );
};

export const granularitySupportsCurrent = (
  granularity?: TimestampGranularity,
) =>
  granularity &&
  [
    TimestampGranularity.Year,
    TimestampGranularity.Month,
    TimestampGranularity.Day,
  ].includes(granularity);

export const RelativeTimeText = ({
  value,
  timestampGranularity,
  isFunnelCondition,
  size,
}: {
  value: any;
  timestampGranularity?: TimestampGranularity;
  isFunnelCondition?: boolean;
  size?: "sm" | "md";
}) => {
  if (isIntervalNow(value)) {
    if (granularitySupportsCurrent(timestampGranularity)) {
      return (
        <Text fontWeight="medium" size={size}>
          this {getGranularityLabel(timestampGranularity)} {value.interval}
        </Text>
      );
    }
    return (
      <Text fontWeight="medium" size={size}>
        now
      </Text>
    );
  }

  return (
    <Box display="flex">
      <Text fontWeight="medium" size={size}>
        {isPresent(value.quantity)
          ? `${String(value.quantity)}${getGranularityLabel(timestampGranularity)} ${pluralize(value.interval, value.quantity, false)}`
          : "___"}
      </Text>
      {!isFunnelCondition && (
        <Text fontWeight="medium" size={size} ml={1}>
          {getRelativeDirectionLabel(value)}
        </Text>
      )}
    </Box>
  );
};

/** This exists to make it easy to mock `new Date()` for tests and avoid needing to mock system time, which can interfere with other libraries. */
export const newDate = (...args: ConstructorParameters<typeof Date> | []) => {
  // @ts-expect-error We're bringing in ancient types from lib.es2015.core.d.ts which has a different signature for date constructor
  return new Date(...args);
};

const getDateValue = (
  value: any,
  timeType: TimeType | undefined,
  timestampGranularity?: TimestampGranularity,
  funnelCondition?: boolean,
  size?: "sm" | "md",
) => {
  return getTimeValue(
    value,
    timeType,
    timestampGranularity,
    funnelCondition,
    true,
    size,
  );
};

const TimeText = ({
  value,
  timeType,
  hideTime,
  isFunnelCondition,
  timestampGranularity,
  size,
}: {
  value: any;
  timeType: TimeType | undefined;
  hideTime?: boolean;
  isFunnelCondition?: boolean;
  timestampGranularity?: TimestampGranularity;
  size?: "sm" | "md";
}) => {
  if (timestampGranularity === TimestampGranularity.Time) {
    return (
      <Text isTruncated fontWeight="medium" size={size}>
        {formatTime(value)}
      </Text>
    );
  } else if (timeType === TimeType.Absolute) {
    return <AbsoluteTimeText hideTime={hideTime} value={value} size={size} />;
  } else {
    return (
      <RelativeTimeText
        isFunnelCondition={isFunnelCondition}
        value={value}
        timestampGranularity={timestampGranularity}
        size={size}
      />
    );
  }
};

export const getRelativeDirectionLabel = (value: IntervalValue) => {
  if (value.direction === RelativeDirection.Forward) {
    return " from now";
  } else {
    return " ago";
  }
};

export const getGranularityLabel = (granularity?: TimestampGranularity) => {
  if (!granularity || granularity === TimestampGranularity.Full) {
    return "";
  }
  return " calendar";
};

export const getTimeValue = (
  value: any,
  timeType: TimeType | undefined,
  timestampGranularity?: TimestampGranularity,
  isFunnelCondition?: boolean,
  hideTime?: boolean,
  size?: "sm" | "md",
) => {
  if (isTimeRangeType(value)) {
    // Time-of-day ranges are excluded: condition validation still enforces
    // bound order for them, so they are never stored reversed.
    const sortableAbsoluteRange =
      timeType === TimeType.Absolute &&
      timestampGranularity !== TimestampGranularity.Time &&
      typeof value.before === "string" &&
      typeof value.after === "string"
        ? { before: value.before, after: value.after }
        : null;
    const [firstValue, secondValue] = sortableAbsoluteRange
      ? sortedAbsoluteRangeBounds(sortableAbsoluteRange).map(
          (bound) => bound.value,
        )
      : [value.before, value.after];

    return (
      <Row>
        <TimeText
          hideTime={hideTime}
          isFunnelCondition={isFunnelCondition}
          timeType={timeType}
          value={firstValue}
          timestampGranularity={timestampGranularity}
          size={size}
        />
        <Text fontWeight="medium" mx={1} size={size}>
          and
        </Text>
        <TimeText
          hideTime={hideTime}
          isFunnelCondition={isFunnelCondition}
          timeType={timeType}
          value={secondValue}
          timestampGranularity={timestampGranularity}
          size={size}
        />
      </Row>
    );
  }

  if (
    (isIntervalType(value) && isCompletedInterval(value)) ||
    timeType === TimeType.Absolute ||
    timestampGranularity === TimestampGranularity.Time
  ) {
    return (
      <TimeText
        hideTime={hideTime}
        isFunnelCondition={isFunnelCondition}
        timeType={timeType}
        value={value}
        timestampGranularity={timestampGranularity}
        size={size}
      />
    );
  }

  return "___";
};

const getTimeRangeValue = (value: NumberRangeValue) => {
  return `${value.min ?? "___"} and ${value.max ?? "___"}`;
};

export const getOperatorLabel = (
  operator: Operator,
  propertyType: ColumnType | null,
  opts: {
    parameterized?: boolean;
    options?: Option[];
  } = {},
) => {
  const {
    parameterized = false,
    options = OperatorOptions[propertyType || ColumnType.Unknown],
  } = opts;
  if (parameterized) return "is";
  return options?.find((option) => option.value === operator)?.label;
};

export const updateConditionOperator = (
  condition: PropertyCondition,
  value: Operator,
) => {
  const diff: Partial<PropertyCondition> = {
    operator: value,
  };

  diff.propertyOptions = {
    ...condition.propertyOptions,
  };
  diff.propertyOptions.timestampGranularity = TimestampGranularity.Full;

  const preserveMemberStateInterval =
    isJourneyMemberStateColumn(condition.property) &&
    isIntervalType(condition.value) &&
    WithinOperators.includes(value);
  if (preserveMemberStateInterval) {
    diff.timeType = TimeType.Relative;
  } else if (shouldResetTimeType(condition.operator, value)) {
    diff.timeType = TimeType.Relative;
    diff.value = null;
  } else if (
    shouldResetValue({
      columnType: getPropertyType(condition),
      oldValue: condition.value,
      oldOperator: condition.operator,
      newOperator: value,
    })
  ) {
    diff.value = null;
  }

  if (shouldResetPercentile(value)) {
    diff.propertyOptions.percentile = false;
  }

  return diff;
};

export const createJourneyEventColumn = ({
  modelId,
  name,
}: {
  modelId: string;
  name: string;
}): RelatedColumn => {
  return {
    type: "related",
    path: [],
    column: {
      type: "journey_event",
      modelId,
      name,
    } satisfies JourneyEventColumn,
  };
};

export const getJourneySetWindowDisplayValue = (
  condition: JourneySetCondition,
) => {
  return (
    <Row gap={1}>
      <Text>
        {journeySetWindowTimestampOperatorOptionsLabelMap.get(
          condition.filter.evalWindow?.operator ?? "any_time",
        )}
      </Text>
      {condition.filter.evalWindow &&
        getTimeValue(
          condition.filter.evalWindow.value,
          condition.filter.evalWindow?.timeType,
          condition.filter.evalWindow?.propertyOptions?.timestampGranularity,
          false,
          false,
        )}
    </Row>
  );
};

const columnRefSchema = Yup.object()
  .shape({
    path: Yup.array()
      .of(Yup.number().required())
      .min(1, "The path array must contain at least one number.")
      .required("Path is required"),
  })
  .required();

const getModelKey = ({
  modelId,
  relationshipId,
}: {
  modelId: string;
  relationshipId?: string;
  // Coerce modelId to a string so filterable-column keys (raw model_id, which
  // may be numeric from the backend) and trait keys (already `.toString()`-ed)
  // collapse to the same key for the same model.
}) => JSON.stringify({ modelId: modelId?.toString(), relationshipId });

const getModelIdFromKey = (modelKey: string) => JSON.parse(modelKey)["modelId"];

/**
 * The same model may appear multiple times in the property selector dropdown
 * if there are multiple relationship paths to the model from the parent model.
 */
const getModelKeyForFilterableColumn = (column: FilterableColumn): string => {
  const { model_id: modelId, column_reference: columnRef } = column;
  let relationshipId: string | undefined = undefined;
  if (columnRefSchema.isValidSync(columnRef)) {
    // Based on the backend implementation, the first item in the path array is the relationship id
    // most closely associated with this column.
    relationshipId = columnRef.path?.at(-1)?.toString();
  }
  return getModelKey({ modelId, relationshipId });
};

const getModelKeyForTrait = (
  trait: TraitDefinitionWithoutFilterableColumnsFragment,
) => {
  // Use to_model for aggregation traits, fall back to segment_id for formula traits
  const modelId =
    trait.relationship?.to_model?.id?.toString() ??
    trait.segment_id?.toString();
  const relationshipId = trait.relationship?.id;
  return getModelKey({ modelId, relationshipId });
};

export function columnsToGroupedOptions<Option = FilterableColumn>(
  columns: FilterableColumn[],
  {
    traits = [],
    columnToOption = (c) => c as Option,
    optionLabel,
    traitToOption,
    traitModelsFilterableColumns,
    modelLabel = ({ modelName }) => modelName,
  }: {
    traits?: TraitDefinitionWithoutFilterableColumnsFragment[];
    columnToOption?: (c: FilterableColumn) => Option;
    optionLabel?: (o: Option) => string;
    traitModelsFilterableColumns?: Record<string, FilterableColumn[]>;
    traitToOption?: (
      t: TraitDefinitionWithoutFilterableColumnsFragment,
      columns: FilterableColumn[],
    ) => Option;
    modelLabel?: (args: {
      modelName: string;
      modelId: string;
      relationshipName?: string;
    }) => string;
  } = {},
) {
  const columnsGroupedByModel = groupBy(
    columns,
    getModelKeyForFilterableColumn,
  );
  const traitsGroupedByModel = groupBy(
    traits.filter((trait) => Boolean(trait.type)),
    getModelKeyForTrait,
  );
  const modelKeys = [
    ...new Set([
      ...Object.keys(columnsGroupedByModel),
      ...Object.keys(traitsGroupedByModel),
    ]),
  ];
  const modelsWithMultiplePaths = getModelsWithMultiplePaths(columns);
  const options = modelKeys.map((modelKey) => {
    const modelId = getModelIdFromKey(modelKey);
    const columns = columnsGroupedByModel[modelKey] ?? [];
    const traits = traitsGroupedByModel[modelKey] ?? [];
    const modelName =
      columns[0]?.model_name ??
      traits[0]?.relationship?.to_model.name ??
      // Fallback for formula traits (no relationship): use parent model name via segment
      traits[0]?.segment?.name ??
      "";
    const relationshipName = modelsWithMultiplePaths.has(modelId)
      ? (columns[0]?.merged_relationship_names?.at(-1) ?? undefined)
      : undefined;
    const label = modelLabel({ modelName, modelId, relationshipName });
    const traitColumns = traitModelsFilterableColumns?.[modelId] ?? [];
    const traitOptions =
      traitToOption && traitColumns
        ? traits.map((trait) => traitToOption(trait, traitColumns))
        : [];
    const columnOptions = columns?.map(columnToOption) ?? [];
    if (optionLabel) {
      traitOptions.sort((a, b) => optionLabel(a).localeCompare(optionLabel(b)));
      columnOptions.sort((a, b) =>
        optionLabel(a).localeCompare(optionLabel(b)),
      );
    }
    return {
      label,
      options: [...traitOptions, ...columnOptions],
    };
  });
  return options;
}

/**
 * Some related models with filterable columns may be reachable multiple times
 * due to multiple relationships. This helps identify them so we can handle them properly
 * when rendering their filterable multiple times.
 */
export const getModelsWithMultiplePaths = (
  columns: FilterableColumnFragment[],
) => {
  const frequencyMap: Record<string, number[][]> = {};
  columns.forEach((c) => {
    const modelId = c.model_id.toString();
    if (!frequencyMap[modelId]) {
      frequencyMap[modelId] = [];
    }
    const path = c.column_reference?.path ?? [];
    if (
      path.length > 0 &&
      !frequencyMap[modelId]!.some((p) => isEqual(p, path))
    ) {
      frequencyMap[modelId]!.push(path);
    }
  });
  const modelsWithMultiplePaths: Set<string> = new Set();
  Object.entries(frequencyMap).forEach(([key, value]) => {
    if (value.length > 1) {
      modelsWithMultiplePaths.add(key);
    }
  });
  return modelsWithMultiplePaths;
};

export const countLeafFilters = (condition: Condition): number => {
  if ("conditions" in condition && Array.isArray(condition.conditions)) {
    return condition.conditions
      .map(countLeafFilters)
      .reduce((el, prev) => prev + el, 0);
  } else {
    return 1;
  }
};

/**
 * Used to display an abbreviate preview of a nested filter
 * Returns an array of the nested and/or filters.
 * Will use the first deepest path if multiple are found.
 */
export const getDirectlyNestedAndOrConditionTypes = (
  condition: Condition,
): (ConditionType.And | ConditionType.Or)[] => {
  if (
    condition.type !== ConditionType.And &&
    condition.type !== ConditionType.Or
  ) {
    return [];
  }

  if (condition.conditions.length === 0) {
    return [condition.type];
  }

  const nestedDepths = condition.conditions.map(
    getDirectlyNestedAndOrConditionTypes,
  );

  let maxIndex = 0;
  for (let i = 1; i < nestedDepths.length; ++i) {
    if (nestedDepths[i]!.length > nestedDepths[maxIndex]!.length) {
      maxIndex = i;
    }
  }

  return [condition.type, ...nestedDepths[maxIndex]!];
};

export const isParentModel = (model: {
  is_schema: boolean | null;
  primary_key: string | null;
  event?: null | Record<string, unknown>;
}) => model.is_schema && model.primary_key && !model.event;

export function useConditionComparison(
  condition: PropertyCondition,
  {
    allowPropertyComparison = true,
  }: { allowPropertyComparison?: boolean } = {},
) {
  const { audienceTraitRhsFilters: audienceTraitRhsFiltersFlag } = useFlags();
  const { restrictions } = useQueryBuilderContext();
  // Trait comparisons can't be resolved when the filter is compiled once
  // into static SQL (e.g. predictive event filters).
  const audienceTraitRhsFilters =
    audienceTraitRhsFiltersFlag && !isDenied(restrictions, "trait-comparisons");
  const isTraitSelected = isConditionComparingToTrait(condition);
  const propertyType = getPropertyType(condition);
  const supportsColumnComparison =
    allowPropertyComparison &&
    isPropertyCondition(condition) &&
    propertyType != null &&
    ColumnComparisonOperatorsByColumnType[propertyType]?.includes(
      condition.operator,
    );

  return {
    audienceTraitRhsFilters,
    isTraitSelected,
    supportsColumnComparison,
    propertyType,
  };
}
